import { useState, useMemo, useCallback, useEffect, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useMusicians } from '../hooks/useMusicians';
import { useProfileContext } from '../hooks/useProfileContext';
import { bandUrlBase, memberUrlPath } from '../utils/slug';
import { apiGetTeam, apiLinkTeamMemberBand, apiMoveRiderCardKind, type TeamMemberDTO } from '../api';
import { reportSaveError, reportSaveOk } from '../lib/saveStatus';
import { useWriteGate } from '../lib/writeGate';
import { showConfirm } from '../components/ConfirmDialog';
import { buildCardDeleteCopy, confirmPersonnelDelete } from '../lib/deletePersonnel';
import PageHeader from '../components/PageHeader';
import { withPageIcon } from '../lib/uiIcons';

export default function MusicianListPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { activeProfileId, profiles, getProfile } = useProfileContext();
  const { bandSlug: urlBandSlug } = useParams();
  // Resolve the band from the URL slug FIRST (the URL is the source of truth
  // for which band's data to show — the context activeProfileId may lag or
  // be stale right after a band switch). Falls back to the context profile.
  const urlProfile = useMemo(
    () => profiles.find((p) => p.slug === urlBandSlug) || null,
    [profiles, urlBandSlug],
  );
  const bandId = urlProfile?.id || activeProfileId || '';
  const activeProfile = bandId ? getProfile(bandId) : null;
  const bandSlug = activeProfile?.slug || bandId || '';
  const base = bandId ? bandUrlBase(bandSlug, activeProfile?.name) : '';
  const { musicians, deleteMusician, reorderMusicians, conflicts, reload: reloadMusicians } =
    useMusicians(bandId || undefined);
  // Write gate: the explicit "Move to Crew" action must be absent for a member
  // who cannot write this band (same boundary as POST /:id/bands).
  const canWrite = useWriteGate(bandId).canWrite;
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [teamMembers, setTeamMembers] = useState<TeamMemberDTO[]>([]);
  const [selectedMemberId, setSelectedMemberId] = useState('');
  const [linking, setLinking] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [showAllBands, setShowAllBands] = useState(false);

  // Load candidates from the Team registry. We fetch the whole registry (not
  // band-scoped) so the "Show members from all bands" toggle can reveal
  // members of other bands for multi-band adds. The default view filters to
  // the selected band only (see availableMembers below).
  useEffect(() => {
    if (!activeProfileId) return;
    let cancelled = false;
    apiGetTeam()
      .then((members) => { if (!cancelled) setTeamMembers(members); })
      .catch(() => { if (!cancelled) setTeamMembers([]); });
    return () => { cancelled = true; };
  }, [activeProfileId, reloadKey]);

  // Default: only members already linked to the selected band (via
  // team_member_bands), and not yet present on this band's rider. When
  // "Show members from all bands" is ticked, reveal every registry member so
  // a person from another band can be added here (multi-band support).
  // A person may be crew in one band and musician in another (user decision
  // 2026-10-12): eligibility for THIS page is NOT the global category. A
  // candidate is a person linked to the band (or any registry member when "all
  // bands" is on) who holds NO card yet in EITHER list of this band — the rider
  // must never count somebody twice. `card_kind` is the server truth per band.
  const availableMembers = useMemo(
    () => teamMembers
      .filter((m) => showAllBands || (m.bands || []).some((b) => b.id === bandId))
      .filter((m) => !(m.bands || []).some((b) => b.id === bandId && b.card_kind))
      // Comfort ordering: people whose GLOBAL category matches this page come
      // first, then everyone else, then by name.
      .sort((a, b) => {
        const ca = a.category === 'musician' ? 0 : 1;
        const cb = b.category === 'musician' ? 0 : 1;
        if (ca !== cb) return ca - cb;
        return a.name.localeCompare(b.name);
      }),
    [teamMembers, bandId, showAllBands],
  );

  // Dropdown label for an "add existing member" candidate: the descriptive role
  // first, then the meaningful (non-category) tags, then a NEUTRAL i18n label —
  // never a category word like "crew".
  const memberRoleLabel = (m: TeamMemberDTO): string => {
    if (m.role && m.role.trim()) return m.role.trim();
    const meaningful = (m.tags || []).filter(
      (tag) => tag !== m.category && tag !== 'musician' && tag !== 'crew',
    );
    if (meaningful.length) return meaningful.join(', ');
    return t('musicians.noRole');
  };

  // Identity sync: the display name comes from the Team registry (nickname ||
  // name) when the rider card shares the same member id — a nickname edited on
  // the Team page shows up here too.
  const teamById = useMemo(() => {
    const map = new Map<string, TeamMemberDTO>();
    for (const m of teamMembers) map.set(m.id, m);
    return map;
  }, [teamMembers]);
  const displayName = (member: { id: string; name?: string }): string => {
    const tm = teamById.get(member.id);
    if (tm?.nickname) return tm.nickname;
    return member.name || t('musicians.unnamed');
  };

  // Option C: Team people linked to THIS band who don't yet have a rider card
  const addExistingMember = async () => {
    if (!bandId || !selectedMemberId) return;
    setLinking(true);
    try {
      // Post the band-specific role: the SERVER mints the card of that kind, so a
      // crew-category person can become a musician in this band.
      const res = await apiLinkTeamMemberBand(selectedMemberId, bandId, 'musician');
      reportSaveOk('musicians');
      // Safety net: if a same-band card of the other kind already exists, no card
      // was minted (never a duplicate). Point the user at the explicit move.
      if (res.card?.conflict) window.alert(t('musicians.moveConflict'));
      setSelectedMemberId('');
      setTeamMembers((prev) => prev.filter((x) => x.id !== selectedMemberId));
      setReloadKey((k) => k + 1);
      await reloadMusicians();
    } catch (err) {
      reportSaveError('musicians', err);
    } finally {
      setLinking(false);
    }
  };

  // Explicit move of a card from this band's Musicians list to its Crew list.
  const moveCard = async (memberId: string) => {
    if (!bandId || !canWrite) return;
    if (!(await showConfirm(t('musicians.moveToCrewConfirm')))) return;
    try {
      await apiMoveRiderCardKind(memberId, bandId, 'crew');
      await reloadMusicians();
      reportSaveOk('musicians');
    } catch (err) {
      reportSaveError('musicians', err);
    }
  };

  const ROLE_RANK: Record<string, number> = { main: 0, session: 1, guest: 2 };
  const ROLE_LABELS: Record<string, string> = {
    main: 'musicians.roleMain',
    session: 'musicians.roleSession',
    guest: 'musicians.roleGuest',
  };
  const [selectedBaseRoles, setSelectedBaseRoles] = useState<string[]>([]);
  const sortedAll = useMemo(
    () =>
      [...musicians].sort((a, b) => {
        const ra = ROLE_RANK[a.baseRole || 'main'] ?? 0;
        const rb = ROLE_RANK[b.baseRole || 'main'] ?? 0;
        if (ra !== rb) return ra - rb;
        return a.order - b.order;
      }),
    [musicians],
  );
  const sorted = useMemo(
    () => (selectedBaseRoles.length === 0
      ? sortedAll
      : sortedAll.filter((m) => selectedBaseRoles.includes(m.baseRole || 'main'))),
    [sortedAll, selectedBaseRoles],
  );

  const conflictList = useMemo(() => conflicts(), [conflicts]);

  const handleDragStart = useCallback(
    (e: React.DragEvent, idx: number) => {
      setDragIdx(idx);
      e.dataTransfer.effectAllowed = 'move';
    },
    [],
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent, dropIdx: number) => {
      e.preventDefault();
      if (dragIdx === null || dragIdx === dropIdx) return;
      const ids = sorted.map((m) => m.id);
      const [moved] = ids.splice(dragIdx, 1);
      ids.splice(dropIdx, 0, moved);
      reorderMusicians(ids);
      setDragIdx(null);
    },
    [dragIdx, sorted, reorderMusicians],
  );

  // LOT A (2026-10-15): this used to be a bare "Delete Musician? / This action
  // cannot be undone." modal. It now names the person, their role in THIS band,
  // and states that the Team registry and their other bands are untouched.
  const confirmDelete = useCallback(async () => {
    if (!deleteId) return;
    const card = musicians.find((m) => m.id === deleteId) || { id: deleteId };
    if (!(await confirmPersonnelDelete(buildCardDeleteCopy(card, 'musician', t)))) return;
    deleteMusician(deleteId);
    setDeleteId(null);
  }, [deleteId, deleteMusician, musicians, t]);

  return (
    <div className="page musician-list-page">
      {/* P3a — unified page header (mono title + sub-line + action right). */}
      <PageHeader
        title={withPageIcon('musicians.title', t('musicians.title'))}
        subtitle={t('musicians.subtitle')}
        actions={
          <button
            className="btn btn-primary"
            onClick={() => navigate(`${base}/musicians/new`)}
          >
            {t('musicians.addMusician')}
          </button>
        }
      />

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', marginBottom: 16 }}>
        <span style={{ color: 'var(--text-dim)', fontSize: 'var(--font-sm)' }}>{t('musicians.baseRole')}</span>
        <button
          type="button"
          className={`rc-pill${selectedBaseRoles.length === 0 ? ' on' : ''}`}
          data-role-filter="all"
          onClick={() => setSelectedBaseRoles([])}
        >
          {t('musicians.all')}
        </button>
        {(['main', 'session', 'guest'] as const).map((br) => (
          <button
            key={br}
            type="button"
            className={`rc-pill${selectedBaseRoles.includes(br) ? ' on' : ''}`}
            data-role-filter={br}
            onClick={() => setSelectedBaseRoles((prev) =>
              prev.includes(br) ? prev.filter((x) => x !== br) : [...prev, br],
            )}
          >
            {t(ROLE_LABELS[br] || br)}
          </button>
        ))}
        <span style={{ color: 'var(--text-muted)', fontSize: 'var(--font-sm)' }}>
          {sorted.length} / {musicians.length}
        </span>
      </div>

      {activeProfileId && (
        <div className="card" style={{ padding: 12, marginBottom: 18, display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div className="form-group" style={{ flex: 2, minWidth: 220, marginBottom: 0 }}>
            <label>{t('musicians.addExistingTeam')}</label>
            <select
              className="form-input"
              value={selectedMemberId}
              onChange={(e) => setSelectedMemberId(e.target.value)}
            >
              <option value="">{t('musicians.choosePerson')}</option>
              {availableMembers.map((m) => (
                <option key={m.id} value={m.id}>{m.name} ({memberRoleLabel(m)})</option>
              ))}
            </select>
          </div>
          <button
            className="btn btn-primary"
            disabled={!selectedMemberId || linking}
            onClick={addExistingMember}
          >
            {linking ? t('musicians.adding') : t('musicians.addToBand')}
          </button>
          <label className="form-check" style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 0, fontSize: '0.85rem' }}>
            <input
              type="checkbox"
              checked={showAllBands}
              onChange={(e) => setShowAllBands(e.target.checked)}
            />
            {t('musicians.showAllBands')}
          </label>
          <span style={{ color: 'var(--text-dim)', fontSize: '0.85rem' }}>
            {availableMembers.length === 0
              ? t('musicians.noTeamLeft')
              : t('musicians.teamAvailable', { count: availableMembers.length })}
          </span>
        </div>
      )}

      {conflictList.length > 0 && (
        <div className="conflict-banner">
          <strong>{t('musicians.conflictsDetected')}</strong>
          <ul>
            {conflictList.map((c, i) => (
              <li key={i}>
                CH {c.channel}: {c.musician1Name} ({c.instrument1Name}) &{' '}
                {c.musician2Name} ({c.instrument2Name})
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="musician-grid">
        {sorted.map((m, idx) => {
          const role = m.baseRole || 'main';
          const prev = idx > 0 ? sorted[idx - 1] : null;
          const prevRole = prev ? prev.baseRole || 'main' : null;
          const isNewSection = idx === 0 || prevRole !== role;
          return (
            <Fragment key={m.id}>
              {isNewSection && (
                <div className="musician-section-label">
                  {t(ROLE_LABELS[role] || role)}
                </div>
              )}
              <div
                className={`musician-card ${dragIdx === idx ? 'dragging' : ''}`}
                data-musician-card={m.id}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, idx)}
                onDragEnd={() => setDragIdx(null)}
              >
                <div
                  className="musician-color-bar"
                  style={{ backgroundColor: m.color }}
                />
                <button
                  type="button"
                  className="musician-drag-handle"
                  draggable
                  onDragStart={(e) => handleDragStart(e, idx)}
                  onDragEnd={() => setDragIdx(null)}
                  title={t('musicians.dragToReorder')}
                  aria-label={t('musicians.dragToReorder')}
                >
                  ⋮⋮
                </button>
                <div
                  className="musician-card-body"
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`${base}/musicians/${memberUrlPath(m.id, m.name)}`)}
                >
                  <h3>{displayName(m)}</h3>
                  <p className="musician-role">{m.fonction || t('musicians.noFunction')}</p>
                  <div className="musician-details">
                    {m.iemType && m.iemType !== 'none' && (
                      <span>{m.iemType === 'stereo' ? t('musicians.stereoIem') : t('musicians.monoIem')}</span>
                    )}
                    {m.wedges > 0 && <span>{t(m.wedges > 1 ? 'musicians.wedges_other' : 'musicians.wedges', { count: m.wedges })}</span>}
                    {m.subwoofer && <span>{t('musicians.sub')}</span>}
                  </div>
                  {(m.tags && m.tags.length > 0) && (
                    <div className="instrument-tags" style={{ marginTop: 4 }}>
                      {m.tags.map(tag => (
                        <span key={tag} className="instrument-tag">{tag}</span>
                      ))}
                    </div>
                  )}
                  <p className="instrument-count">
                    {t(m.instruments.length !== 1 ? 'musicians.instruments_other' : 'musicians.instruments', { count: m.instruments.length })}
                  </p>
                  {m.instruments.length > 0 && (
                    <div className="instrument-tags">
                      {m.instruments.map(inst => (
                        <span key={inst.id} className="instrument-tag">{inst.name || t('musicians.unnamed')}</span>
                      ))}
                    </div>
                  )}
                  <div className="musician-card-actions">
                    <button
                      className="btn btn-small"
                      onClick={(e) => { e.stopPropagation(); navigate(`${base}/musicians/${memberUrlPath(m.id, m.name)}`); }}
                    >
                      {t('musicians.edit')}
                    </button>
                    {canWrite && (
                      <button
                        className="btn btn-small"
                        data-move-card={m.id}
                        onClick={(e) => { e.stopPropagation(); moveCard(m.id); }}
                      >
                        {t('musicians.moveToCrew')}
                      </button>
                    )}
                    <button
                      className="btn btn-small btn-danger"
                      onClick={(e) => { e.stopPropagation(); setDeleteId(m.id); void confirmDelete(); }}
                    >
                      {t('musicians.delete')}
                    </button>
                  </div>
                </div>
              </div>
            </Fragment>
          );
        })}
        {sorted.length === 0 && (
          <div className="empty-state">
            <p>{t('musicians.noMusicians')}</p>
          </div>
        )}
      </div>

      {/* LOT A (2026-10-15): the hand-rolled modal that lived here is gone —
          deletion now goes through the shared showConfirm bus (ConfirmDialog),
          which carries the named consequences. `confirmDelete` is still wired to
          the row's Delete button above. */}
    </div>
  );
}
